<template>
  <!-- ⭐ Create Dialog ⭐ -->
  <el-dialog
    id="create-modal"
    class="form-modal"
    :visible.sync="createVisible"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="create-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Create Short Numbers</h1>
        <div class="modal-actions col-2">
          <el-button
            v-if="!bulkOperationComplete"
            type="primary"
            @click="createOperation"
            :disabled="!bulkShortNumbers.length"
            >Create
          </el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="handleClose"
            >{{ bulkOperationComplete ? "Close" : "Cancel" }}</el-button
          >
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="handleClose"
          ></el-button>
        </div>
      </div>
    </div>

    <div class="dialog__body">
      <div id="operation-summary" v-if="bulkOperationComplete">
        <div v-if="!!successCount" class="inline-form__group">
          <el-button type="success" icon="el-icon-check" size="mini">
            <span class="black-text">
              {{ successCount }}
            </span>
          </el-button>
        </div>
        <div v-if="!!errorCount" class="inline-form__group">
          <el-button type="danger" icon="el-icon-close" size="mini">
            <span class="black-text">
              {{ errorCount }}
            </span> </el-button
          >&nbsp;
        </div>
      </div>

      <el-form v-if="showFormOnCreate">
        <h2 class="form-section-header">
          Add numbers
        </h2>
        <div class="inline-form-group">
          <el-col :span="11">
            <el-input v-model="number" placeholder="Number"
              ><template slot="prefix"><span>Number</span></template>
              <template slot="append" v-if="numUsed">
                <in-use :inUse="numInUse" />
              </template>
            </el-input>
          </el-col>
          <el-col :span="1" />
          <el-col :span="12">
            <el-input
              placeholder="Enter destination number"
              v-model="createForm.destnumber"
            >
              <template slot="prefix">
                Destination number
              </template>
            </el-input>
          </el-col>
        </div>
        <el-input
          placeholder="Enter description"
          v-model="createForm.description"
        >
          <template slot="prefix">
            Description
          </template>
        </el-input>
        <div class="inline-form-group">
          <el-col :span="11" />
          <el-col :span="5" />
          <el-col :span="8" style="text-align:right;">
            <el-tooltip content="Add" placement="top" v-if="fieldsValid">
              <el-button
                circle
                type="primary"
                :disabled="numUsed"
                @click="addShortnumber"
              >
                <el-icon class="el-icon-plus" />
              </el-button>
            </el-tooltip>
            <el-button
              @click="clearNumberFields"
              type="danger"
              v-if="fieldsValid"
            >
              <el-icon type="danger" class="el-icon-delete" />
            </el-button>
          </el-col>
        </div>
        <h2 class="form-section-header">
          Bulk import csv
        </h2>

        <div id="shortnumbers-summary-block">
          <div class="inline-form-group mt-20">
            <bulk-example :file-path="bulkFileExample">
              <el-button type="primary" @click="startImport" slot="hoverRef">
                Import from csv
              </el-button>
            </bulk-example>

            <el-button
              class="ml-5"
              type="danger"
              icon="el-icon-close"
              v-if="fileSelected"
              @click="closeImporting"
            />
            <el-button
              class="ml-5"
              type="primary"
              icon="el-icon-check"
              v-if="csvReady"
              @click="addToBulk"
            />
          </div>
          <div class="csv-import" v-show="importVisible">
            <upload-csv
              :key="uploadCsvKey"
              ref="fileButton"
              v-model="parsedCSV"
              :map-fields="[
                { label: 'Number', key: 'number' },
                { label: 'Destination Number', key: 'destnumber' },
                { label: 'Description', key: 'description' }
              ]"
              @input="csvReady = true"
              @fileSelected="submitForMapping"
              @invalidFileType="handleInvalidFileType"
              :headers="true"
            />
          </div>
        </div>
      </el-form>
      <div class="vxe-table mt-20" v-if="bulkShortNumbers.length">
        <vxe-grid
          round
          class="bulk-numbers-table"
          border="inner"
          height="350px"
          auto-resize
          empty-text="No Data"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :data="bulkShortNumbers"
          :columns="tableColumns"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script>
import feathersClient from "../../../../feathers/feathers-client";
import uploadCSV from "@/components/upload-csv";
import InUse from "@/components/InUse";
import bulkExample from "@/components/bulkExample";
import { mapActions } from "vuex";
import {phoneNumberFormatter} from "@/globals/globals";

export default {
  name: "ShortNumbersCreateForm",

  props: {
    numInUse: { required: true },
    createVisible: { required: true }
  },

  components: {
    UploadCsv: uploadCSV,
    bulkExample,
    InUse
  },

  data() {
    return {
      bulkFileExample:
        "https://global-app.s3.amazonaws.com/omnia-assets/bulk_short_numbers_example.csv",
      uploadCsvKey: "",
      showFormOnCreate: true,
      number: "",
      successCount: 0,
      errorCount: 0,
      bulkOperationComplete: false,
      closeModalContext: "Cancel and Discard Unsaved Changes?",
      createForm: {
        number: "",
        destnumber: "",
        description: ""
      },
      // ⭐️ Bulk Data ⭐️ //
      bulkShortNumbers: [],
      parsedCSV: null,
      fieldsValid: false,
      importVisible: false,
      csvReady: false,
      fileSelected: false,
      form: {
        number: "",
        destnumber: "",
        description: ""
      },
      tableColumns: [
        { type: "seq", width: 30 },
        {
          field: "number",
          title: "Number",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.number}</span>];
            }
          }
        },
        {
          field: "destnumber",
          title: "Destination",
          minWidth: 150,
          sortable: true,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.numberFormatter(row.destnumber)}</span>];
            }
          }
        },
        {
          field: "status",
          title: "",
          slots: {
            default: ({ row }) => {
              return [
                <div>
                  {row.status === "done" && (
                    <el-button
                      type="success"
                      class="table-action-button"
                      icon="el-icon-check"
                      size="mini"
                    />
                  )}
                  {row.status === "loading" && (
                    <el-button
                      type="primary"
                      class="table-action-button"
                      icon="el-icon-loading"
                      size="mini"
                    />
                  )}
                  {row.status === "error" && (
                    <el-tooltip placement="top" content={row.errorMessage}>
                      <el-button
                        type="danger"
                        class="table-action-button"
                        icon="el-icon-close"
                        size="mini"
                      />
                    </el-tooltip>
                  )}
                </div>
              ];
            }
          }
        },
        {
          field: "remove",
          title: "",
          slots: {
            default: ({ row, rowIndex }) => {
              return [
                <div>
                  {row.status === "" && (
                    <el-button
                      onClick={() => this.removeFromBulk(rowIndex)}
                      class="table-action-button"
                      type="default"
                      icon="el-icon-minus"
                      size="mini"
                    />
                  )}
                </div>
              ];
            }
          }
        }
      ]
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    numUsed() {
      return this.numInUse && this.numInUse.inUse;
    },

    fieldLength() {
      let correctFieldCount = 0;
      Object.keys(this.createForm).forEach(key => {
        if (this.createForm[key]) {
          correctFieldCount++;
        }
      });
      return correctFieldCount;
    }
  },

  watch: {
    number(val) {
      if (val) {
        this.createForm.number = val;
        this.$emit("checkForAvailableNum", val);
      }
    },

    fieldLength() {
      this.fieldsValid = this.fieldLength === 3;
    },

    bulkOperationComplete(val) {
      if (val) {
        this.closeModalContext = `Close the bulk create modal?`;
      }
    }
  },

  methods: {
    ...mapActions("short-numbers", {
      createShortNumbers: "create"
    }),

    addShortnumber() {
      const createBody = { ...this.createForm };
      createBody.destnumber = createBody.destnumber.replace("(", "");
      createBody.destnumber = createBody.destnumber.replace(")", "");
      createBody.destnumber = createBody.destnumber.replace(/-/g, "");
      this.bulkShortNumbers.push({
        ...createBody,
        status: "",
        errorMessage: ""
      });
      this.number = "";
      this.createForm = {
        number: "",
        destnumber: "",
        description: ""
      };
    },

    handleClose() {
      this.cancelOperation();
    },

    cancelOperation() {
      this.clearFields();
      this.$emit("closeModal");
    },

    clearFields() {
      this.uploadCsvKey = "";
      this.number = "";
      this.showFormOnCreate = true;
      this.errorCount = 0;
      this.successCount = 0;
      this.bulkShortNumbers = [];
      this.createForm = {
        number: "",
        destnumber: "",
        description: ""
      };

      this.bulkOperationComplete = false;
      this.closeModalContext = "Cancel and discard unsaved changes?";

      this.parsedCSV = null;
      this.csvReady = false;
      this.fileSelected = false;
      this.importVisible = false;
      this.fieldsValid = false;
      this.bulkShortNumbers = [];
      this.form = {
        number: "",
        destnumber: "",
        description: ""
      };
    },

    // ⭐️ Methods for Bulk Operation ⭐️ //
    createOperation() {
      this.$confirm("Create short numbers?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel"
      })
        .then(async () => {
          this.showFormOnCreate = false;
          await this.saveShortNumbers();
        })
        .catch(() => true);
    },

    async saveShortNumbers() {
      if (this.bulkShortNumbers.length) {
        for (let i = 0; i < this.bulkShortNumbers.length; i++) {
          this.bulkShortNumbers[i].status = "loading";
          await this.wait(500);
          const { number } = this.bulkShortNumbers[i];
          if (!number) {
            this.bulkShortNumbers[i].errorMessage = "Number cannot be empty!";
            this.bulkShortNumbers[i].status = "error";
            this.errorCount++;
            continue;
          }

          const { inUse } = await feathersClient.service("check-number").find({
            query: {
              tenantId: this.selectedTenant.id,
              number
            }
          });
          if (!inUse) {
            const createData = {
              tenantId: this.selectedTenant.id,
              ...this.bulkShortNumbers[i]
            };
            delete createData.status;
            delete createData.errorMessage;
            delete createData._XID;

            this.createShortNumbers(createData)
              .then(res => {
                this.bulkShortNumbers[i].status = "done";
                this.successCount++;
              })
              .catch(error => {
                if (
                  error.hasOwnProperty("errors") &&
                  Object.keys(error.errors).length !== 0
                ) {
                  this.bulkShortNumbers[i].errorMessage =
                    error.errors[Object.keys(error.errors)[0]];
                } else {
                  this.bulkShortNumbers[i].errorMessage = error.message;
                }
                this.bulkShortNumbers[i].status = "error";
                this.errorCount++;
              });
          } else {
            this.bulkShortNumbers[i].status = "error";
            this.errorCount++;
            this.bulkShortNumbers[i].errorMessage = "Number already in use!";
          }
        }
        await this.wait(500);
        this.bulkOperationComplete = true;
      }
    },

    async wait(ms) {
      return new Promise(resolve => {
        setTimeout(resolve, ms);
      });
    },

    closeImporting() {
      this.parsedCSV = null;
      this.csvReady = false;
      this.fileSelected = false;
      this.importVisible = false;
      this.$refs.fileButton.sample = null;
      this.$refs.fileButton.fileSelected = false;
      this.$refs.fileButton.map = {};
      this.$refs.fileButton.isValidFileMimeType = false;
      this.$refs.fileButton.form = { csv: null };
      let fileButton = document.querySelector(".form-control-file");
      fileButton.value = null;
    },

    addToBulk() {
      const values = [...this.parsedCSV];
      for (let i = 0; i < values.length; i++) {
        this.bulkShortNumbers.push({
          ...values[i],
          status: "",
          errorMessage: ""
        });
      }
      this.$emit("bulkCreate", this.bulkShortNumbers);
      this.closeImporting();
    },

    startImport() {
      this.importVisible = true;
      const fileButton = document.querySelector(".form-control-file");
      fileButton.click();
    },

    removeFromBulk(index) {
      this.bulkShortNumbers.splice(index, 1);
    },

    submitForMapping() {
      this.fileSelected = true;
      this.$refs.fileButton.load();
    },

    getRandomKey() {
      const randomString = () =>
        Math.random()
          .toString(36)
          .slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },

    handleInvalidFileType() {
      this.closeImporting();
      this.uploadCsvKey = this.getRandomKey();
      this.$notify({
        title: "Invalid file type",
        message: "Please import csv files only!",
        type: "error"
      });
    },

    clearNumberFields() {
      this.createForm = {
        destnumber: "",
        description: "",
        number: ""
      };
    },

    numberFormatter(number) {
      return number
        ? phoneNumberFormatter(number)
        : number;
    }
  },

  created() {
    this.uploadCsvKey = this.getRandomKey();
  }
};
</script>

<style lang="scss" scoped>
#shortnumbers-summary-block {
  max-width: 600px;
  margin: 10px auto;
}

#operation-summary {
  max-width: 600px;
  margin: 10px auto;

  span {
    font-size: 18px;
  }
}

#error-summary {
  max-width: 600px;
  margin: 30px auto;
}

.modal-summary-table {
  max-width: 600px;
  margin: 20px auto;
}

.vxe-table {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.vue-csv-uploader {
  input[type="file"] {
    display: none;
  }
}
</style>
